import { useNewLeadsCampaignModalStore } from '@forwardflow/store/modal/NewLeadsCampaignModal'
import { message, Modal } from 'antd'
import { useState, useRef, useEffect, useMemo } from 'react'
import { getFilterIcon, FilterType } from '../../utils/filterIcons'
import Icon from '@forwardflow/ui/Icons'
import CampaignService from '@forwardflow/services/CampaignService'
import { CampaignData } from '@forwardflow/types'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Button } from '@forwardflow/ui'
import { useGetFubSources } from '@forwardflow/hooks/customHooks/stageMapping/sourceList'
import {
   useGetActiveLeadSource,
   useGetActiveLeadSourceDetails,
} from '@forwardflow/hooks/customHooks'

interface FilterRow {
   id: string
   type: FilterType
   sources?: string[]
}

export const NewLeadCampaignModal = () => {
   const navigate = useNavigate()
   const [searchParams] = useSearchParams()
   const isEditMode = searchParams.has('edit')
   const { title: leadSource } = useParams()
   const { data: activeLeadSourceData, mutate: mutateActiveLeadSource } =
      useGetActiveLeadSource(leadSource as string)
   const hasCampaignFiltersChanges =
      activeLeadSourceData?.draft?.changedSections?.includes('campaign_filters')
   const { data: campaignDetails } = useGetActiveLeadSourceDetails(
      leadSource as string
   )
   const { isNewLeadsCampaignModalOpen, closeNewLeadsCampaignModal } =
      useNewLeadsCampaignModalStore()
   const [campaignName, setCampaignName] = useState('')
   const [isLoading, setIsLoading] = useState(false)
   const [campaignNameError, setCampaignNameError] = useState('')
   const [queryParams, setQueryParams] = useState({
      page: 1,
      limit: 20,
      search: '',
   })
   const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
   const [isSearching, setIsSearching] = useState(false)
   const [sourceSearchQuery, setSourceSearchQuery] = useState('')
   const [isLoadingMore, setIsLoadingMore] = useState(false)
   const [totalSources, setTotalSources] = useState(0)
   const { data: dataSource } = useGetFubSources(queryParams)

   useEffect(() => {
      if (
         dataSource &&
         dataSource?.meta &&
         dataSource?.meta?.totalItems > 0 &&
         queryParams.page === 1
      ) {
         setTotalSources(1000)
      }
   }, [dataSource, queryParams.page])

   const handleSourceDropdownScroll = (e: React.UIEvent<HTMLDivElement>) => {
      const bottom =
         e.currentTarget.scrollHeight - e.currentTarget.scrollTop ===
         e.currentTarget.clientHeight

      if (
         bottom &&
         !isLoadingMore &&
         dataSource &&
         dataSource?.meta &&
         dataSource?.meta?.totalItems < totalSources
      ) {
         setIsLoadingMore(true)
         setQueryParams((prev) => ({
            ...prev,
            page: prev.page + 1,
         }))
      }
   }

   useEffect(() => {
      if (isLoadingMore && dataSource) {
         setIsLoadingMore(false)
      }
   }, [dataSource, isLoadingMore])

   const [filterRows, setFilterRows] = useState<FilterRow[]>([
      { id: '1', type: 'source', sources: [] },
   ])
   const [openDropdowns, setOpenDropdowns] = useState<{
      [key: string]: boolean
   }>({})
   const dropdownRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})
   const [accumulatedSources, setAccumulatedSources] = useState<any[]>([])

   useEffect(() => {
      if (dataSource) {
         if (isLoadingMore) {
            setAccumulatedSources((prev) => [
               ...prev,
               ...(dataSource.data || []),
            ])
            setIsLoadingMore(false)
         } else if (queryParams.page === 1) {
            setAccumulatedSources(dataSource.data || [])
         }
         setIsSearching(false)
      }
   }, [dataSource, isLoadingMore, queryParams.page])

   const sourceOptions = accumulatedSources?.map((source) => ({
      label: source.campaignName || source.leadSource,
      value: source.campaignName || source.leadSource,
      count: source.totalLeads || 0,
   }))

   const filteredSourceOptions = useMemo(() => {
      if (!sourceSearchQuery) return sourceOptions
      return (
         sourceOptions?.filter((option) =>
            option.label.toLowerCase().includes(sourceSearchQuery.toLowerCase())
         ) || []
      )
   }, [sourceOptions, sourceSearchQuery])

   useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
         Object.entries(dropdownRefs.current).forEach(([key, ref]) => {
            if (ref && !ref.contains(event.target as Node)) {
               setOpenDropdowns((prev) => ({ ...prev, [key]: false }))
            }
         })
      }

      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
   }, [])

   const toggleDropdown = (key: string) => {
      setOpenDropdowns((prev) => ({
         ...prev,
         [key]: !prev[key],
      }))
   }

   const handleSourceToggle = (rowId: string, value: string) => {
      setFilterRows((prev) =>
         prev.map((row) => {
            if (row.id === rowId && row.sources) {
               const sources = row.sources.includes(value)
                  ? row.sources.filter((item) => item !== value)
                  : [...row.sources, value]
               return { ...row, sources }
            }
            return row
         })
      )
   }

   const handleSourceSelectAll = (rowId: string) => {
      setFilterRows((prev) =>
         prev.map((row) => {
            if (row.id === rowId && row.sources) {
               if (sourceSearchQuery) {
                  const currentSources = new Set(row.sources)
                  const visibleValues = new Set(
                     filteredSourceOptions.map((o) => o.value)
                  )
                  const allVisibleSelected = filteredSourceOptions.every((o) =>
                     currentSources.has(o.value)
                  )

                  if (allVisibleSelected) {
                     return {
                        ...row,
                        sources: row.sources.filter(
                           (s) => !visibleValues.has(s)
                        ),
                     }
                  } else {
                     const newSources = [...row.sources]
                     filteredSourceOptions.forEach((o) => {
                        if (!currentSources.has(o.value)) {
                           newSources.push(o.value)
                        }
                     })
                     return { ...row, sources: newSources }
                  }
               } else {
                  return {
                     ...row,
                     sources:
                        row.sources.length === sourceOptions?.length
                           ? []
                           : sourceOptions?.map((o) => o.value),
                  }
               }
            }
            return row
         })
      )
   }

   useEffect(() => {
      const anySourceDropdownOpen = Object.entries(openDropdowns).some(
         ([key, isOpen]) => key.startsWith('source-') && isOpen
      )

      if (!anySourceDropdownOpen) {
         setSourceSearchQuery('')
      }
   }, [openDropdowns])

   const handleSourceSearch = (searchText: string) => {
      setSourceSearchQuery(searchText)

      if (searchTimeoutRef.current) {
         clearTimeout(searchTimeoutRef.current)
      }

      searchTimeoutRef.current = setTimeout(() => {
         if (searchText !== queryParams.search) {
            setIsSearching(true)
            setAccumulatedSources([])
            setQueryParams((prev) => ({
               ...prev,
               page: 1,
               search: searchText,
            }))
         }
      }, 300)
   }

   useEffect(() => {
      return () => {
         if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current)
         }
      }
   }, [])

   const getSelectedSourcesText = (sources: string[]) => {
      if (sources.length === 0) return `Select Source`
      if (sources.length === 1) {
         return sourceOptions?.find((option) => option.value === sources[0])
            ?.label
      }
      return `${sources.length} sources selected`
   }

   const renderFilterControl = (row: FilterRow) => {
      return (
         <>
            <div
               className="relative flex-1"
               ref={(el) => (dropdownRefs.current[`source-${row.id}`] = el)}
            >
               <div
                  className="bg-[#2a2a2a] text-white px-4 py-2 rounded-md focus:outline-none cursor-pointer flex items-center h-[62px]"
                  onClick={() => toggleDropdown(`source-${row.id}`)}
               >
                  <span
                     className={`flex-1 ${
                        !row.sources?.length ? 'text-gray-400' : 'text-white'
                     }`}
                  >
                     {getSelectedSourcesText(row.sources || [])}
                  </span>
                  <Icon name="arrow-down" className="fill-white" />
                  {(row.sources?.length || 0) > 0 && (
                     <span className="flex items-center justify-center w-5 h-5 ml-2 text-xs bg-purple-600 rounded-full">
                        {row.sources?.length}
                     </span>
                  )}
               </div>

               {openDropdowns[`source-${row.id}`] && (
                  <div
                     className="absolute mt-1 w-full bg-[#2a2a2a] border border-gray-700 rounded-md z-10 shadow-lg max-h-[300px] overflow-y-auto"
                     onScroll={handleSourceDropdownScroll}
                  >
                     <div className="sticky top-0 bg-[#2a2a2a] p-2 border-b border-gray-700">
                        <div className="relative">
                           <input
                              type="text"
                              className="w-full bg-[#333] text-white px-3 py-2 pl-8 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500"
                              placeholder="Search sources..."
                              onChange={(e) =>
                                 handleSourceSearch(e.target.value)
                              }
                              value={sourceSearchQuery || ''}
                              onClick={(e) => e.stopPropagation()}
                           />

                           <Icon
                              name="search"
                              className="absolute w-2 h-2 fill-gray-400 scale-75 left-1.5 top-[8px]"
                              width={12}
                              height={12}
                           />
                        </div>
                     </div>

                     <div
                        className="flex items-center px-4 py-2 border-b border-gray-700 cursor-pointer hover:bg-[#333] sticky top-[52px] bg-[#2a2a2a]"
                        onClick={() => handleSourceSelectAll(row.id)}
                     >
                        <div
                           className={`w-4 h-4 rounded border mr-3 flex items-center justify-center ${
                              row.sources?.length ===
                              filteredSourceOptions.length
                                 ? 'bg-purple-600 border-purple-600'
                                 : 'border-gray-500'
                           }`}
                        >
                           {row.sources?.length === sourceOptions?.length && (
                              <svg
                                 className="w-3 h-3 text-white"
                                 fill="currentColor"
                                 viewBox="0 0 20 20"
                                 xmlns="http://www.w3.org/2000/svg"
                              >
                                 <path
                                    fillRule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                 />
                              </svg>
                           )}
                        </div>
                        <span>Select all</span>
                        <span className="ml-auto text-gray-400">
                           Total leads
                        </span>
                     </div>

                     <div className="overflow-y-auto">
                        {filteredSourceOptions?.map((option) => (
                           <div
                              key={`source-option-${row.id}-${option.value}`}
                              className="flex items-center px-4 py-2 cursor-pointer hover:bg-[#333]"
                              onClick={() =>
                                 handleSourceToggle(row.id, option.value)
                              }
                           >
                              <div
                                 className={`w-4 h-4 rounded border mr-3 flex items-center justify-center ${
                                    row.sources?.includes(option.value)
                                       ? 'bg-purple-600 border-purple-600'
                                       : 'border-gray-500'
                                 }`}
                              >
                                 {row.sources?.includes(option.value) && (
                                    <svg
                                       className="w-3 h-3 text-white"
                                       fill="currentColor"
                                       viewBox="0 0 20 20"
                                       xmlns="http://www.w3.org/2000/svg"
                                    >
                                       <path
                                          fillRule="evenodd"
                                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                          clipRule="evenodd"
                                       />
                                    </svg>
                                 )}
                              </div>
                              <span>{option?.label}</span>
                              <span className="ml-auto text-gray-400">
                                 {option?.count?.toLocaleString()}
                              </span>
                           </div>
                        ))}

                        {isLoadingMore && (
                           <div className="flex items-center justify-center py-2 text-gray-400">
                              Loading more...
                           </div>
                        )}

                        {filteredSourceOptions?.length === 0 &&
                           !isLoadingMore &&
                           !isSearching && (
                              <div className="py-4 text-center text-gray-400">
                                 No sources match your search.
                              </div>
                           )}

                        {isSearching && (
                           <div className="flex items-center justify-center p-2 text-gray-400 bg-[#2a2a2a] sticky top-[52px] border-b border-gray-700">
                              Searching...
                           </div>
                        )}
                     </div>
                  </div>
               )}
            </div>
         </>
      )
   }

   const renderClearButton = (row: FilterRow) => {
      const hasSelection = (row.sources?.length || 0) > 0

      if (!hasSelection) return null

      return (
         <button
            className="text-gray-400 hover:text-white"
            onClick={() => {
               setFilterRows((prev) =>
                  prev.map((r) => (r.id === row.id ? { ...r, sources: [] } : r))
               )
            }}
         >
            <svg
               className="w-5 h-5"
               fill="none"
               stroke="currentColor"
               viewBox="0 0 24 24"
               xmlns="http://www.w3.org/2000/svg"
            >
               <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
               />
            </svg>
         </button>
      )
   }

   const prepareCampaignData = (): CampaignData => {
      const campaignData = {
         campaignName: campaignName || undefined,
         filters: filterRows
            .map((row) => {
               if (row.sources && row.sources.length > 0) {
                  return {
                     type: 'source',
                     sources: row.sources,
                     field: 'source',
                     logic: 'AND',
                     value: row.sources,
                     operator: 'CONTAINS_ANY',
                  }
               }
               return null
            })
            .filter((filter) => filter !== null) as any,
      }

      if (!campaignData.campaignName) {
         delete campaignData.campaignName
      }

      return campaignData
   }

   const validateCampaignName = () => {
      if (!campaignName.trim()) {
         setCampaignNameError('Campaign name is required')
         return false
      }
      setCampaignNameError('')
      return true
   }

   const [estimatedLeads, setEstimatedLeads] = useState<number>(0)
   const [isLoadingEstimate, setIsLoadingEstimate] = useState(false)

   useEffect(() => {
      const getEstimate = async () => {
         try {
            setIsLoadingEstimate(true)
            const campaignData = prepareCampaignData()

            const hasFilters =
               campaignData.filters && campaignData.filters.length > 0

            if (hasFilters) {
               const response = await CampaignService.getEstimatedLeads(
                  campaignData
               )
               setEstimatedLeads(response?.count || 0)
            } else {
               setEstimatedLeads(0)
            }
         } catch (error) {
            console.error('Failed to get estimated leads:', error)
            setEstimatedLeads(0)
         } finally {
            setIsLoadingEstimate(false)
         }
      }

      const timeoutId = setTimeout(() => {
         getEstimate()
      }, 500)

      return () => clearTimeout(timeoutId)
   }, [filterRows])

   useEffect(() => {
      if (isEditMode && activeLeadSourceData) {
         setCampaignName(activeLeadSourceData.campaignName || '')

         const dataSource =
            hasCampaignFiltersChanges && campaignDetails
               ? campaignDetails
               : activeLeadSourceData

         if (
            dataSource?.data?.campaignFilters?.eligibleCriteria &&
            Array.isArray(dataSource?.data?.campaignFilters?.eligibleCriteria)
         ) {
            const sourceFilters =
               dataSource?.data?.campaignFilters?.eligibleCriteria.filter(
                  (criteria: any) => criteria.field === 'source'
               )

            if (sourceFilters.length > 0) {
               const sources = sourceFilters[0].value || []
               setFilterRows([
                  {
                     id: '1',
                     type: 'source',
                     sources: sources,
                  },
               ])
            }
         }
      }
   }, [
      isEditMode,
      activeLeadSourceData,
      hasCampaignFiltersChanges,
      campaignDetails,
   ])

   return (
      <>
         <Modal
            className="p-0 ff-modal !w-[850px]"
            title=""
            open={isNewLeadsCampaignModalOpen}
            onCancel={closeNewLeadsCampaignModal}
            footer={null}
         >
            <div className="px-8 pb-3 mb-5 border-b border-white/20 pt-7">
               <p className="text-[20px] font-medium mb-4 leading-6">
                  {isEditMode ? 'Edit Campaign Filters' : 'New Leads Campaign'}
               </p>
            </div>
            <div className="px-8 mb-6">
               <div className="flex items-center justify-between mb-5">
                  <span>Campaign name</span>
                  <span>
                     Estimated Reach:{' '}
                     {isLoadingEstimate ? (
                        <span className="animate-pulse">Loading...</span>
                     ) : (
                        `${estimatedLeads.toLocaleString()} Leads`
                     )}
                  </span>
               </div>

               <input
                  type="text"
                  className={`w-full h-[62px] bg-[#2a2a2a] text-white px-4 py-2 rounded-md focus:outline-none focus:ring-2 ${
                     campaignNameError
                        ? 'border border-red-500 focus:ring-red-500'
                        : 'focus:ring-purple-500'
                  }`}
                  placeholder="Campaign Name"
                  value={campaignName}
                  onChange={(e) => {
                     setCampaignName(e.target.value)
                     if (e.target.value.trim()) setCampaignNameError('')
                  }}
                  onBlur={validateCampaignName}
               />
               {campaignNameError && (
                  <p className="mt-1 text-sm text-red-500">
                     {campaignNameError}
                  </p>
               )}
            </div>
            <div className="px-8">
               <p className="mb-2">Select Lead Sources</p>
               <p className="mb-4 text-[12px] text-white/70">
                  Select Lead Sources where you want to
               </p>

               {filterRows.map((row) => (
                  <div
                     key={`filter-row-${row.id}`}
                     className="flex items-center gap-2 mb-4"
                  >
                     <div
                        className="relative"
                        ref={(el) =>
                           (dropdownRefs.current[`type-${row.id}`] = el)
                        }
                     >
                        <div className="flex items-center bg-[#2a2a2a] px-4 py-2 rounded-md cursor-pointer h-[62px] min-w-[180px]">
                           {getFilterIcon(row.type)}
                           <span className="text-white capitalize">
                              {row.type}
                           </span>
                        </div>
                     </div>

                     {renderFilterControl(row)}

                     {renderClearButton(row)}
                  </div>
               ))}
            </div>
            <div className="flex justify-end gap-3 p-4 mt-6 border-t border-gray-700">
               <button
                  className="h-12 px-4 py-2 font-semibold text-white rounded-md bg-white/5 hover:bg-gray-600 w-44"
                  onClick={closeNewLeadsCampaignModal}
               >
                  Cancel
               </button>

               <Button
                  isLoading={isLoading}
                  className="flex items-center justify-center gap-2 px-4 py-2 !mt-0 font-semibold text-white !rounded-md bg-primary !w-44 hover:bg-purple-700"
                  onClick={async () => {
                     if (!validateCampaignName()) return

                     setIsLoading(true)
                     const campaignData = prepareCampaignData()

                     console.log('Campaign Data:', campaignData)
                     try {
                        let responseActiveLeadSource: any
                        let responseUpdatedLeadSource: any
                        if (isEditMode && activeLeadSourceData?.id) {
                           responseActiveLeadSource =
                              await CampaignService.editCustomActiveLeadSource(
                                 activeLeadSourceData?.id,
                                 campaignData
                              )
                           responseUpdatedLeadSource =
                              await CampaignService.updateCampaignName(
                                 activeLeadSourceData?.id,
                                 campaignName
                              )
                        } else {
                           responseActiveLeadSource =
                              await CampaignService.createCustomActiveLeadSource(
                                 campaignData?.campaignName as string,
                                 campaignData,
                                 'new-lead'
                              )
                        }
                        if (responseUpdatedLeadSource) {
                           message.success('Campaign updated successfully')

                           const newSearchParams = new URLSearchParams(
                              searchParams
                           )
                           newSearchParams.set(
                              'campaignName',
                              responseUpdatedLeadSource?.campaignName
                           )
                           navigate(
                              `${
                                 window.location.pathname
                              }?${newSearchParams.toString()}`,
                              { replace: true }
                           )
                        }
                        if (
                           responseActiveLeadSource &&
                           responseActiveLeadSource.id
                        ) {
                           if (isEditMode) {
                              message.success('Campaign updated successfully')
                           } else {
                              navigate(
                                 `/campaign/${encodeURIComponent(
                                    responseActiveLeadSource.id
                                 )}/phone-number?campaignName=${campaignName}`
                              )
                           }
                        }
                     } catch (e) {
                        message.error(
                           isEditMode
                              ? 'Failed to update campaign'
                              : 'Failed to create campaign'
                        )
                     } finally {
                        closeNewLeadsCampaignModal()
                        setIsLoading(false)
                        mutateActiveLeadSource()
                     }
                  }}
               >
                  <Icon name="TourIcon" />
                  {isEditMode ? 'Update' : 'Activate'}
               </Button>
            </div>
         </Modal>
      </>
   )
}
